import { Card, CardContent, Grid, Typography, styled } from "@mui/material";
import { IPosition, IUser } from "@vmosolutions/oc-interfaces";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { gotCrewLedgers } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { formatDateString } from "../DateDisplay";

// Crew Selector imports
import { SelectField } from "src/components/FormikComponents/SelectField";
import { formatCrewList } from "src/components/Admin/Tabs/Crew/ManageCrew";
import { PositionBadge } from "src/components/CrewSchedule/PositionBadge";
import { formatUserLabel } from "src/components/Admin/Tabs/Users/ManageUsers";
import { SpinnerProgress } from "../SpinnerProgress/SpinnerProgress";
import { ICrewEventLedgerMap } from "src/redux/reducers/CrewScheduleReducer";
import WarningIcon from "@mui/icons-material/Warning";

// Pairing Display components
import { ShortCodeChip } from "src/components/ShortCodes/ShortCodeChip";

const OCCrewLedger = styled(Card)(({ theme }) => ({
  width: "100%",
  height: "100%",
  "& .content": {
    width: "100%",
    height: "100%",
    "& .title": { fontSize: 18, margin: 5 },
    "& .crewledger_navigation": {
      maxWidth: 400,
      "& .navigation_btn": {
        border: "1px solid #607D95",
        borderRadius: 5,
        padding: 5,
        margin: 5,
      },
      "& .crewledger_container": {
        margin: "10px 20px",
        "& .event_most_recent_change": {
          marginTop: 10,
          paddingTop: 10,
          borderTop: "2px solid white",
          "& .event_change_title": { fontSize: 16, margin: "5px 0px" },
        },
      },
    },
  },
}));

interface ICrewLedgerProps {
  crewId: string;
}

export const CrewLedger = (props: ICrewLedgerProps): JSX.Element | null => {
  const dispatch = useDispatch();
  const { crewId } = props;
  const [loading, setLoading] = useState<boolean>(false);

  // Actively Selected Crew Member
  const positions: IPosition[] = useSelector((state: IRootState) => state.structures.customerStructure.positions).sort(
    (a, b) => (b.displayOrder > a.displayOrder ? -1 : 1)
  );

  const [users, setUsers] = useState<IUser[]>([]);
  const [groupedCrewList, setGroupedCrewList] = useState<any>([]);
  const [activeCrew, setActiveCrew] = useState<IUser>();
  const activeCrewId = activeCrew?._id;
  const crewName = activeCrew
    ? `${activeCrew?.lastName}, ${activeCrew?.firstName} ${activeCrew?.employeeId ? `(${activeCrew.employeeId})` : ""}`
    : "unknown";

  useEffect(() => {
    const getUsersAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.GetPaginate<IUser>("/users");
      if (Array.isArray(response)) {
        setUsers(response);
      }
    };
    getUsersAction();
  }, []);

  // If positions are users are updated, rebuilt crew list.
  useEffect(() => {
    setGroupedCrewList(formatCrewList(users, positions));
  }, [positions, users]);

  // Set default user is crewId is passed by window props and users are loaded and activeUser is null.
  useEffect(() => {
    if (crewId && users.length > 0) {
      const crewMember = users.find((crew) => crew._id === crewId) ?? null;
      setActiveCrew(crewMember);
    }
  }, [crewId, users]);

  // Crew Ledgers for all crew members (stored in redux)
  const allCrewLedgers: ICrewEventLedgerMap = useSelector((state: IRootState) => state.crewSchedule.crewLedgers);

  // Return all ledgers belonging to specific crew member sorted in descending "createdAt".
  const crewLedgers = useMemo(() => {
    return allCrewLedgers[activeCrewId]
      ? allCrewLedgers[activeCrewId].sort((a, b) => b.ledger[0].createdAt?.valueOf() - a.ledger[0].createdAt?.valueOf())
      : [];
  }, [activeCrewId, allCrewLedgers]);

  useEffect(() => {
    const fetchCrewMemberLedger = async (): Promise<void> => {
      setLoading(true);
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `Loading Crew Pairing Ledger for ${crewName}...`,
        successMessage: `${crewName}'s Crew Pairing Ledger loaded successfully`,
        errorMessage: `Error: Failed to load Crew Pairing Ledger for ${crewName}`,
      };
      const fetchedCrewLedger: any[] | null = await AmplifyAPIWrapper.Get(
        `/crew/ledger/${activeCrew?._id}`,
        notifications
      );
      if (Array.isArray(fetchedCrewLedger)) {
        dispatch(gotCrewLedgers(fetchedCrewLedger, activeCrew?._id));
      }
      setLoading(false);
    };
    if (activeCrew?._id) {
      fetchCrewMemberLedger();
    }
  }, [activeCrew, crewName, dispatch]);

  return (
    <OCCrewLedger>
      <CardContent className="content">
        <Typography variant="h5" className="title">
          Event Ledgers
        </Typography>
        <SpinnerProgress
          loading={loading}
          message={`Loading Crew Pairing Ledger for ${crewName}...`}
          fullscreen={false}
        />

        <Grid item style={{ marginBottom: 20, minWidth: 300, maxWidth: 400 }}>
          <SelectField
            name="crew"
            label="Crew Member"
            options={groupedCrewList}
            value={activeCrew}
            onChange={setActiveCrew}
            getOptionLabel={formatUserLabel}
            formatOptionLabel={(user: IUser) => {
              const { firstName, lastName, employeeId, crewInfo } = user;
              const highPos = crewInfo?.positions?.sort((a, b) => a.displayOrder - b.displayOrder)[0];
              return (
                <Grid container className="crew_option_container" alignItems="center" wrap="nowrap">
                  {highPos && <PositionBadge position={highPos} filled={true} isInactive={!user?.crewInfo?.isActive} />}
                  <Grid item style={{ marginLeft: 10 }}>{`${firstName} ${lastName} ${
                    employeeId ? `[${employeeId}]` : ""
                  }`}</Grid>
                </Grid>
              );
            }}
            getOptionValue={(user: IUser) => user._id}
            placeholder="Select Crew Member..."
            isSearchable={true}
            isClearable={true}
          />
        </Grid>

        <Grid
          container
          direction="column"
          style={{ maxHeight: "calc(100% - 80px)", flexWrap: "nowrap", overflowY: "auto" }}
        >
          {crewLedgers?.length > 0 ? (
            crewLedgers.map((item) => {
              return (
                <Grid key={`ledger_entry_${item._id}`} container direction="column" style={{ marginBottom: 10 }}>
                  <Grid container>
                    <Grid item style={{ width: 60 }}>
                      {item.eventType === "PAIRING" ? (
                        <ShortCodeChip code={item.name} itemId={item.eventId} type="pairing" />
                      ) : (
                        <Typography
                          variant="caption"
                          style={{ fontSize: 12 }}
                        >{`${item.eventType} - ${item.name}`}</Typography>
                      )}
                    </Grid>

                    <Grid container direction="column" style={{ width: "calc(100% - 60px)" }}>
                      <Typography color="textSecondary" style={{ fontSize: 12, marginLeft: 10 }}>
                        {`${item?.ledger[0]?.message ?? ""} ${formatDateString(item?.ledger[0].createdAt, "Etc/UTC")}`}
                      </Typography>
                      {!item.acknowledged && (
                        <Grid container style={{ width: "auto", marginLeft: 10 }} alignItems="center">
                          <WarningIcon color="secondary" style={{ width: 12, height: 12 }} />
                          <Typography color="secondary" style={{ fontSize: 12, marginLeft: 5 }}>
                            Missing Acknowledgment
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              );
            })
          ) : activeCrewId ? (
            <Typography>{`There were no event ledgers found for ${crewName}.`}</Typography>
          ) : (
            <Typography>{"Please select a crew member to view available ledgers."}</Typography>
          )}
        </Grid>
      </CardContent>
    </OCCrewLedger>
  );
};
